<template>
  <v-sheet class="mainUsersSetting">
    <resizable-page>
      <template v-slot:header>
        <setting-header />
        <section class="mainUsersSetting_actions">
          <app-text-field
            v-model="query"
            class="mainUsersSetting_search"
            color="dark"
            aria-autocomplete="off"
            autocomplete="off"
            outlined
            full-width
            :hide-details="true"
            placeholder="ユーザーの検索"
          />
          <v-btn class="mainUsersSetting_new" @click="handleNew">
            ユーザーの新規登録</v-btn
          >
        </section>
      </template>
      <template v-slot:table="tableProps">
        <v-skeleton-loader
          v-if="loading || routeLoading"
          :loading="loading || routeLoading"
          type="table"
        ></v-skeleton-loader>
        <v-data-table
          v-else
          class="mainUsersSetting_tbl"
          :height="tableProps.bodyHeight"
          :headers="computedHeaders"
          :items="tableItems"
          hide-default-footer
          disable-pagination
          sort-by="dateTime"
          :fixed-header="true"
        >
          <!-- Table body template  -->
          <template v-slot:item="{ item }">
            <tr @click="!item.owner && handleEditUser(item.id)">
              <td class="text-start">
                <tooltip :classes="'fixed-content'">
                  <template v-slot:activator="{ on, attrs }">
                    <div
                      v-bind="attrs"
                      class="mainUsersSetting_tbl-name text-truncate"
                      style="max-width: 100%"
                      v-on="on"
                    >
                      {{ item.name }}
                      <span
                        v-if="item.owner"
                        class="mainUsersSetting_name-owner"
                      >
                        組織管理者
                      </span>
                    </div>
                  </template>
                  <h2>
                    {{ item.name }}
                  </h2>
                </tooltip>
              </td>
              <td class="text-start">
                <tooltip :classes="'fixed-content'">
                  <template v-slot:activator="{ on, attrs }">
                    <div
                      v-bind="attrs"
                      class="mainUsersSetting_tbl-registerd text-truncate"
                      style="max-width: 100%"
                      v-on="on"
                    >
                      {{ item.email }}
                    </div>
                  </template>
                  <h2>{{ item.email }}</h2>
                </tooltip>
              </td>
              <td class="text-start">
                <tooltip :classes="'fixed-content'">
                  <template v-slot:activator="{ on, attrs }">
                    <div
                      v-bind="attrs"
                      class="mainUsersSetting_tbl-account text-truncate"
                      style="max-width: 100%"
                      v-on="on"
                    >
                      {{ item.group }}
                    </div>
                  </template>
                  <h2>{{ item.group }}</h2>
                </tooltip>
              </td>
            </tr>
          </template>
          <!-- ----------------- -->
          <!-- Table no data -->
          <template v-slot:no-data>
            <tr style="visibility: hidden" />
          </template>
          <!-- ----------------- -->
        </v-data-table>
      </template>
    </resizable-page>
    <!-- Message dialog -->
    <clip-dialog-message
      :open="!!dialogMessageText"
      :message="dialogMessageText"
      @onClose="closeDialogMessage"
    />
    <!-- ----------------- -->
  </v-sheet>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import SettingHeader from '../SettingHeader.vue';
import ClipDialogMessage from '@/components/dialogs/ClipDialogMessage.vue';

export default {
  name: 'MainUsersSetting',
  components: {
    SettingHeader,
    ClipDialogMessage,
  },
  props: {
    routeLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dialogMessageText: '',
      cameraGroupLoading: false,
      loading: false,
      query: null,
      tableItems: [],
    };
  },
  computed: {
    ...mapGetters('settings', {
      users: 'usersWithOwner',
    }),
    ...mapState({
      items: (state) => state.settings.groupCams,
      currentAvatars: (state) => state.settings.currentAvatars,
    }),
    currentUserAvatarId() {
      return this.currentAvatars ? this.currentAvatars.avatars[0].id : null;
    },
    computedHeaders() {
      return [
        {
          text: '名前',
          align: 'start',
          sortable: false,
          value: 'name',
          width: '130',
          class: 'mainUsersSetting_header-name',
        },
        {
          text: 'ID',
          align: 'start',
          sortable: false,
          value: 'email',
          width: '150',
        },
        {
          text: 'カメラグループ',
          align: 'start',
          sortable: false,
          value: 'group',
          width: '150',
        },
      ];
    },
  },
  watch: {
    dialogMessageText(val) {
      val || this.closeDialogMessage();
    },
    users(val) {
      if (val && val.length) {
        this.initializeTableItems();
      }
    },
    query(val) {
      if (val) {
        this.tableItems = this.users
          .filter((user) => {
            return user.name.toLowerCase().includes(val.toLowerCase());
          })
          .map((u) => {
            return {
              id: u.id,
              email: u.email,
              name: u.name,
              group: '',
              owner: u.owner,
            };
          });
      } else {
        this.initializeTableItems();
      }
    },
  },
  mounted() {
    if (this.$route.query.dl === 1) {
      this.displayDeleteMessage();
    }
    this.init();
    this.initializeTableItems();
  },
  methods: {
    ...mapActions('settings', [
      'fetchUserAvatarList',
      'setNewUserCamList',
      'fetchCameraGroup',
    ]),
    handleNew() {
      this.setNewUserCamList([]);
      this.navigateTo({
        name: 'settings-users-new',
      });
    },
    async init() {
      if (this.items.length < 1) {
        await this.fetchCameraGroup();
      }
    },
    async displayDeleteMessage() {
      this.dialogMessageText = 'ユーザー情報を削除しました。';
      await this.fetchUserAvatarList();
      this.$router.replace({ query: null });
    },
    closeDialogMessage() {
      this.dialogMessageText = null;
    },
    async handleEditUser(id) {
      this.navigateTo({
        name: 'settings-users-edit',
        params: { userId: id },
      });
    },
    navigateTo(to) {
      this.$router.push(to);
    },
    initializeTableItems() {
      this.tableItems = this.users.map((u) => {
        return {
          id: u.id,
          email: u.email,
          name: u.name,
          owner: u.owner,
          group: u.groupCams?.groupCamList,
        };
      });
    },
  },
};
</script>

<style lang="scss">
.mainUsersSetting {
  &_actions {
    margin-top: 20px;
    display: flex;
    justify-content: space-between;
  }
  &_name-owner {
    font-size: 12px;
    font-weight: 500;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.75;
    letter-spacing: normal;
    text-align: left;
    color: #989898;
    margin-left: 10px;
  }
  &_new {
    font-size: 14px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.14;
    letter-spacing: normal;
    text-align: left;
    border-radius: 5px;
    width: 172px;
    height: 36px;
    box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.16);
    &:disabled {
      color: #ccc;
      background-color: #f3f3f3;
    }
  }
  &_header-name {
    max-width: 250px;
  }
  &_search {
    margin-bottom: 0;
    margin-top: 0;
    padding-top: 0;
    max-width: 327px;
    &.v-input {
      font-size: 18px;
      .v-messages:not(.error--text) {
        .v-messages__message {
          font-size: 12px;
          font-weight: normal;
          font-stretch: normal;
          font-style: normal;
          line-height: 1.5;
          letter-spacing: normal;
          text-align: left;
        }
      }
    }
    .v-text-field__slot {
      padding-left: 0;
    }

    .v-input__slot {
      min-height: 36px !important;
      margin-bottom: 2px;
      .v-input__append-inner {
        margin-top: 9px;
      }
      .v-message__message {
        font-size: 12px;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        line-height: 1.5;
        letter-spacing: normal;
        text-align: left;
        color: #989898;
      }
    }
    &.theme--light.v-input input {
      font-size: 16px;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.38;
      letter-spacing: normal;
      text-align: left;
      &:-webkit-autofill,
      &:-webkit-autofill::first-line {
        font-family: 'Noto Sans JP', sans-serif;
        font-size: 16px;
        font-weight: normal;
      }
    }

    .v-text-field__details {
      padding: 0 !important;
      padding-top: 0px !important;
      margin-bottom: 0 !important;
    }
  }
  &-infinit_scroll {
    text-align: center;
  }
  &.theme--light.v-sheet {
    border-radius: 0;
    box-shadow: none;
    padding: 0;
  }
  &_gap-header {
    height: 26px;
  }
  &_select {
    &-icon {
      width: 34px;
      height: 34px;
      padding: 5px 5px 5px 5px;
      background-color: #0075c2;
      border-radius: 50%;
      display: table-cell;
      vertical-align: middle;
      text-align: center;
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate3d(-50%, -50%, 0);
      .icon-check {
        color: #fff;
        font-size: 25px;
        font-weight: 900;
      }
    }
  }
  &_tbl {
    &.v-data-table {
      margin-top: 15px;
    }
    &-registerd,
    &-account {
      font-size: 14px;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.5;
      letter-spacing: normal;
      color: #4b4b4b;
    }
  }
  th {
    vertical-align: middle;
    min-width: 130px;
    span {
      font-family: 'Noto Sans JP', sans-serif;
      font-size: 12px;
      font-weight: bold;
      color: #4b4b4b;
    }
  }
  td {
    vertical-align: middle;
    max-width: 75px;
    .noThumbnailImage {
      margin: 10px;
    }
  }
  tr {
    cursor: pointer;
    &.selected {
      background-color: #f4f6fc !important;
      border-color: #f4f6fc !important;
    }
  }
  &_btn {
    margin-right: 5px;
  }
  display: flex;
  flex-direction: column;
  &_subtitle {
    font-size: 12px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.67;
    letter-spacing: normal;
    text-align: left;
    color: #989898;
  }
  &_title {
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 2px;
    color: var(--text-color);
    max-width: 966px;
    @media (min-width: 1280px) {
      width: calc(68vw - 339.77px);
    }
    &_text {
      font-size: 20px;
      font-weight: bold;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.5;
      letter-spacing: normal;
      text-align: left;
      color: #4b4b4b;
    }
    &_type {
      color: var(--warning-color-lighten1);
      font-size: 12px;
      font-weight: 600;
    }
  }
  &_videos {
    position: relative;
  }
  .v-btn {
    &:not(.v-btn--round).v-size--x-small {
      padding-left: 8px;
      padding-right: 8px;
      &.v-btn--outlined {
        padding-left: 7px;
        padding-right: 7px;
        color: #989898;
        border-color: #989898;
      }
    }
    &:not(:last-child) {
      margin-right: 8px;
    }
  }
  & .v-skeleton-loader {
    margin-top: 10px;
  }
  & .v-skeleton-loader__table-heading,
  & .v-skeleton-loader__table-tfoot {
    display: none;
  }
  .v-data-table__wrapper {
    @include scrollBarCustom;
  }
}
</style>
